import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useToast } from '@/hooks/use-toast';

// UUID validation regex - accepts any valid UUID format
const UUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export const usePositValidation = (positId: string | undefined, redirectOnInvalid = true) => {
  const navigate = useNavigate();
  const { toast } = useToast();

  const isValidUUID = (id: string): boolean => {
    return UUID_REGEX.test(id);
  };

  const isValidPositId = positId ? isValidUUID(positId) : false;

  useEffect(() => {
    if (positId && !isValidPositId && redirectOnInvalid) {
      
      // Enhanced logging for debugging
      const debugInfo = {
        original: positId,
        userAgent: navigator.userAgent,
        url: window.location.href,
        referrer: document.referrer,
        timestamp: new Date().toISOString(),
        // Check if this looks like a user handle instead of UUID
        looksLikeHandle: positId.includes(' ') || positId.length < 30,
        isAlphabetic: /^[a-zA-Z]+$/.test(positId),
        pathSegments: window.location.pathname.split('/'),
        currentRoute: window.location.pathname
      };
      
      
      // Check if user was trying to access a profile page
      if (debugInfo.isAlphabetic && debugInfo.currentRoute.includes('/chat/')) {
        toast({
          title: "Routing Error",
          description: `It looks like you were trying to access a user profile. Redirecting to the correct page.`,
          variant: "destructive",
        });
        
        // Redirect to profile page instead
        setTimeout(() => {
          navigate(`/p/${positId}`, { replace: true });
        }, 1000);
        return;
      }
      
      // Clear any React Query cache that might contain bad data
      try {
        const queryClient = (window as any).__reactQueryClient;
        if (queryClient) {
          queryClient.clear();
        }
      } catch (error) {
      }
      
      toast({
        title: "Invalid Posit ID",
        description: `The posit ID "${positId}" appears to be invalid. Redirecting to a safe page.`,
        variant: "destructive",
      });

      // Redirect to dashboard after a short delay
      setTimeout(() => {
        navigate('/dashboard', { replace: true });
      }, 2000);
    }
  }, [positId, isValidPositId, redirectOnInvalid, navigate, toast]);

  return {
    isValidPositId,
    positId: isValidPositId ? positId : undefined,
  };
};

export const validatePositId = (id: string | undefined): boolean => {
  if (!id) return false;
  return UUID_REGEX.test(id);
}; 